import { Hidden }                         from "@material-ui/core";
import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector }       from "react-redux";
import { Form, Label, Message }           from "semantic-ui-react";
import Customer                           from "../../../domain/customer/Customer";
import CustomerBirthDate                  from "../../../domain/customer/CustomerBirthDate";
import CustomerBirthMonth                 from "../../../domain/customer/CustomerBirthMonth";
import CustomerBirthYear                  from "../../../domain/customer/CustomerBirthYear";
import CustomerNumber                     from "../../../domain/customer/CustomerNumber";
import CustomerRepository                 from "../../../domain/customer/CustomerRepository";
import Email                              from "../../../domain/fundamentals/email/Email";
import FirstName                          from "../../../domain/fundamentals/name/FirstName";
import FirstNameKana                      from "../../../domain/fundamentals/name/FirstNameKana";
import LastName                           from "../../../domain/fundamentals/name/LastName";
import LastNameKana                       from "../../../domain/fundamentals/name/LastNameKana";
import Phone                              from "../../../domain/fundamentals/phone/Phone";
import { ResKaState }                     from "../../../index";
import { apiGetCustomerHasAccount }       from "../../../infrastructure/api/customer";
import CustomerDataSource                 from "../../../infrastructure/datasource/customer/CustomerDataSource";
import AddressFormField                   from "../common/AddressFormField";
import GenderSelector                     from "../common/GenderSelector";
import ValidatorMessage                   from "../message/ValidatorMessage";

const CustomerForm: FC = () => {
  const customer: Customer = useSelector((state: ResKaState) => state.customer);
  const [hasAccount, setHasAccount] = useState(false);
  const repository: CustomerRepository = new CustomerDataSource(useDispatch());

  useEffect(() => {
    apiGetCustomerHasAccount(customer.id).then(value => setHasAccount(value));
  }, []);

  return (
    <>
      {
        hasAccount
          ?
          <>
            <Message warning>
              <Message.Header>この顧客情報はWEB予約ページにて作成されました。</Message.Header>
              <p>編集できない項目はWEB予約ページで顧客本人しか編集できません。</p>
            </Message>
          </>
          :
          <></>
      }
      <Form>
        <Hidden smDown>
          <Form.Group>
            <Form.Field>
              <label>顧客番号</label>
              <Form.Input
                defaultValue={ customer.detail.number.value }
                onChange={ (event, data) => repository.setCustomerNumber(new CustomerNumber(data.value)) }
              />
              <ValidatorMessage target="detail.number"/>
            </Form.Field>
          </Form.Group>

          <Form.Group>
            <Form.Field required>
              <label>氏名(姓)</label>
              <Form.Input
                defaultValue={ customer.detail.name.lastName.value }
                onChange={ (event, data) => repository.setCustomerLastName(new LastName(data.value)) }
                disabled={ hasAccount }
              />
              <ValidatorMessage target="detail.last_name"/>
            </Form.Field>
            <Form.Field required>
              <label>氏名(名)</label>
              <Form.Input
                defaultValue={ customer.detail.name.firstName.value }
                onChange={ (event, data) => repository.setCustomerFirstName(new FirstName(data.value)) }
                disabled={ hasAccount }
              />
              <ValidatorMessage target="detail.first_name"/>
            </Form.Field>
          </Form.Group>

          <Form.Group>
            <Form.Field required>
              <label>氏名カナ(姓)</label>
              <Form.Input
                defaultValue={ customer.detail.name.lastNameKana.value }
                onChange={ (event, data) => repository.setCustomerLastNameKana(new LastNameKana(data.value)) }
                disabled={ hasAccount }
              />
              <ValidatorMessage target="detail.last_name_kana"/>
            </Form.Field>
            <Form.Field required>
              <label>氏名カナ(名)</label>
              <Form.Input
                defaultValue={ customer.detail.name.firstNameKana.value }
                onChange={ (event, data) => repository.setCustomerFirstNameKana(new FirstNameKana(data.value)) }
                disabled={ hasAccount }
              />
              <ValidatorMessage target="detail.first_name_kana"/>
            </Form.Field>
          </Form.Group>

          <Form.Group>
            <GenderSelector
              genderType={ customer.detail.genderType }
              onChange={ genderType => repository.setCustomerGenderType(genderType) }
              disabled={ hasAccount }
            />
          </Form.Group>

          <Form.Group>
            <Form.Field>
              <label>生年月日</label>
              <Form.Input
                labelPosition="right"
                defaultValue={ customer.birthday.year.value }
                onChange={ (event, data) => repository.setCustomerBirthdayYear(new CustomerBirthYear(data.value)) }
              >
                <Label basic>西暦</Label>
                <input/>
                <Label basic>年</Label>
              </Form.Input>
              <ValidatorMessage target="birthday.year"/>
            </Form.Field>
            <Form.Field>
              <label>&nbsp;</label>
              <Form.Input
                labelPosition="right"
                defaultValue={ customer.birthday.month.value }
                onChange={ (event, data) => repository.setCustomerBirthdayMonth(new CustomerBirthMonth(data.value)) }
              >
                <input/>
                <Label basic>月</Label>
              </Form.Input>
              <ValidatorMessage target="birthday.month"/>
            </Form.Field>
            <Form.Field>
              <label>&nbsp;</label>
              <Form.Input
                labelPosition="right"
                defaultValue={ customer.birthday.date.value }
                onChange={ (event, data) => repository.setCustomerBirthdayDate(new CustomerBirthDate(data.value)) }
              >
                <input/>
                <Label basic>日</Label>
              </Form.Input>
              <ValidatorMessage target="birthday.date"/>
            </Form.Field>
          </Form.Group>

          <Form.Group>
            <Form.Field>
              <label>電話番号</label>
              <Form.Input
                type="tel"
                defaultValue={ customer.contact.phone.value }
                onChange={ (event, data) => repository.setCustomerPhone(new Phone(data.value)) }
                disabled={ hasAccount }
              />
              <ValidatorMessage target="contact.phone_number"/>
            </Form.Field>
          </Form.Group>

          <Form.Group>
            <Form.Field>
              <label>Eメール</label>
              <Form.Input
                type="email"
                defaultValue={ customer.contact.email.value }
                onChange={ (event, data) => repository.setCustomerEmail(new Email(data.value)) }
                disabled={ hasAccount }
              />
              <ValidatorMessage target="contact.email"/>
            </Form.Field>
          </Form.Group>

          <AddressFormField
            isGroup
            address={ customer.address }
            setPostalCode={ postalCode => repository.setCustomerPostalCode(postalCode) }
            setPrefecture={ prefecture => repository.setCustomerPrefecture(prefecture) }
            setCity={ city => repository.setCustomerCity(city) }
            setStreet={ street => repository.setCustomerStreet(street) }
            setBuilding={ building => repository.setCustomerBuilding(building) }
          />
        </Hidden>

        <Hidden mdUp>
          <Form.Field>
            <label>顧客番号</label>
            <Form.Input
              defaultValue={ customer.detail.number.value }
              onChange={ (event, data) => repository.setCustomerNumber(new CustomerNumber(data.value)) }
            />
            <ValidatorMessage target="detail.number"/>
          </Form.Field>

          <Form.Field required>
            <label>氏名(姓)</label>
            <Form.Input
              defaultValue={ customer.detail.name.lastName.value }
              onChange={ (event, data) => repository.setCustomerLastName(new LastName(data.value)) }
              disabled={ hasAccount }
            />
            <ValidatorMessage target="detail.last_name"/>
          </Form.Field>
          <Form.Field required>
            <label>氏名(名)</label>
            <Form.Input
              defaultValue={ customer.detail.name.firstName.value }
              onChange={ (event, data) => repository.setCustomerFirstName(new FirstName(data.value)) }
              disabled={ hasAccount }
            />
            <ValidatorMessage target="detail.first_name"/>
          </Form.Field>

          <Form.Field required>
            <label>氏名カナ(姓)</label>
            <Form.Input
              defaultValue={ customer.detail.name.lastNameKana.value }
              onChange={ (event, data) => repository.setCustomerLastNameKana(new LastNameKana(data.value)) }
              disabled={ hasAccount }
            />
            <ValidatorMessage target="detail.last_name_kana"/>
          </Form.Field>
          <Form.Field required>
            <label>氏名カナ(名)</label>
            <Form.Input
              defaultValue={ customer.detail.name.firstNameKana.value }
              onChange={ (event, data) => repository.setCustomerFirstNameKana(new FirstNameKana(data.value)) }
              disabled={ hasAccount }
            />
            <ValidatorMessage target="detail.first_name_kana"/>
          </Form.Field>

          <GenderSelector
            genderType={ customer.detail.genderType }
            onChange={ genderType => repository.setCustomerGenderType(genderType) }
            disabled={ hasAccount }
          />

          <Form.Field>
            <label>生年月日</label>
            <Form.Input
              labelPosition="right"
              defaultValue={ customer.birthday.year.value }
              onChange={ (event, data) => repository.setCustomerBirthdayYear(new CustomerBirthYear(data.value)) }
            >
              <Label basic>西暦</Label>
              <input/>
              <Label basic>年</Label>
            </Form.Input>
            <ValidatorMessage target="birthday.year"/>
          </Form.Field>
          <Form.Field>
            <label>&nbsp;</label>
            <Form.Input
              labelPosition="right"
              defaultValue={ customer.birthday.month.value }
              onChange={ (event, data) => repository.setCustomerBirthdayMonth(new CustomerBirthMonth(data.value)) }
            >
              <input/>
              <Label basic>月</Label>
            </Form.Input>
            <ValidatorMessage target="birthday.month"/>
          </Form.Field>
          <Form.Field>
            <label>&nbsp;</label>
            <Form.Input
              labelPosition="right"
              defaultValue={ customer.birthday.date.value }
              onChange={ (event, data) => repository.setCustomerBirthdayDate(new CustomerBirthDate(data.value)) }
            >
              <input/>
              <Label basic>日</Label>
            </Form.Input>
            <ValidatorMessage target="birthday.date"/>
          </Form.Field>

          <Form.Field>
            <label>電話番号</label>
            <Form.Input
              type="tel"
              defaultValue={ customer.contact.phone.value }
              onChange={ (event, data) => repository.setCustomerPhone(new Phone(data.value)) }
              disabled={ hasAccount }
            />
            <ValidatorMessage target="contact.phone_number"/>
          </Form.Field>

          <Form.Field>
            <label>Eメール</label>
            <Form.Input
              type="email"
              defaultValue={ customer.contact.email.value }
              onChange={ (event, data) => repository.setCustomerEmail(new Email(data.value)) }
              disabled={ hasAccount }
            />
            <ValidatorMessage target="contact.email"/>
          </Form.Field>

          <AddressFormField
            address={ customer.address }
            setPostalCode={ postalCode => repository.setCustomerPostalCode(postalCode) }
            setPrefecture={ prefecture => repository.setCustomerPrefecture(prefecture) }
            setCity={ city => repository.setCustomerCity(city) }
            setStreet={ street => repository.setCustomerStreet(street) }
            setBuilding={ building => repository.setCustomerBuilding(building) }
          />
        </Hidden>
      </Form>
    </>
  );
};

export default CustomerForm;
